import { useMemo, useState } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsTags, MRTGrid, Typography } from "@foodpilot/foods";
import type { MRT_ColumnDef, MRT_PaginationState, MRT_Row, MRT_SortingState } from "material-react-table";
import { useTranslation } from "react-i18next";
import { ACTION_PLAN_TEMPLATE, CREATE, useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    ACTION_PLAN_TEMPLATES_PAGE_SIZE,
    useActionPlanTemplates,
    type ActionPlanTemplate,
    type TemplateSort,
    type TemplateSortField,
} from "src/api/client-api/actionPlanTemplates/useActionPlanTemplates";
import { URLHandler } from "src/services/URLHandler";
import { SettingsPageHeader } from "../components/SettingsPageHeader";
import { useNavigate } from "react-router";
import { CreateTemplateDialog } from "./CreateTemplateDialog";
import { TemplateStatusBadge } from "./TemplateStatusBadge";
import { TemplateActionsMenu } from "./TemplateActionsMenu";

export const ActionPlanTemplates = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [creating, setCreating] = useState(false);
    const openTemplate = (id: string) =>
        navigate(URLHandler.settings.configurationPath("platform-setup", "action-plan-templates", id));
    const theme = useTheme();
    const { canUser } = useFoodpilotContext();
    const canCreate = canUser(CREATE, ACTION_PLAN_TEMPLATE);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: ACTION_PLAN_TEMPLATES_PAGE_SIZE,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const sort: TemplateSort | null =
        sorting[0] ?
            {
                field: sorting[0].id as TemplateSortField,
                direction: sorting[0].desc ? "desc" : "asc",
            }
        :   null;
    const query = useActionPlanTemplates(pagination.pageIndex + 1, sort);
    const templates = query.data?.["hydra:member"] ?? [];
    const total = query.data?.["hydra:totalItems"];
    const title = t("settings.action_plan_templates.title", "Action plan templates");

    const columns = useMemo<MRT_ColumnDef<ActionPlanTemplate>[]>(() => {
        const definitions: MRT_ColumnDef<ActionPlanTemplate>[] = [
            {
                accessorKey: "name",
                header: t("settings.action_plan_templates.column.name", "Template name"),
                size: 280,
            },
            {
                accessorKey: "status",
                header: t("settings.action_plan_templates.column.status", "Status"),
                size: 140,
                Cell: (props: { row: MRT_Row<ActionPlanTemplate> }) => (
                    <TemplateStatusBadge status={props.row.original.status} />
                ),
            },
            {
                accessorKey: "sectorName",
                header: t("settings.action_plan_templates.column.sector", "Industry sector"),
                size: 200,
            },
            {
                accessorKey: "ssqTitle",
                header: t("settings.action_plan_templates.column.ssq", "SSQ"),
                size: 200,
                Cell: (props: { row: MRT_Row<ActionPlanTemplate> }) => (
                    <Typography>
                        {props.row.original.ssqTitle ??
                            t("settings.action_plan_templates.ssq_unavailable", "SSQ unavailable")}
                    </Typography>
                ),
            },
            {
                accessorKey: "actionCount",
                header: t("settings.action_plan_templates.column.action_count", "Number of actions"),
                size: 200,
            },
        ];
        return definitions;
    }, [t, theme]);

    return (
        <Box sx={{ padding: { xs: theme.spacing(2), md: theme.spacing(4) } }}>
            <SettingsPageHeader
                title={title}
                subtitle={t(
                    "settings.action_plan_templates.description",
                    "Create action plan templates to offer to users.",
                )}
                breadcrumbs={[
                    { label: t("settings.platform.title", "Platform"), to: URLHandler.settings.configuration() },
                    {
                        label: t("settings.platform.platform_setup.title", "Platform setup"),
                        to: URLHandler.settings.configurationPath("platform-setup"),
                    },
                    { label: title },
                ]}
            />
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                gap={theme.spacing(2)}
                sx={{ mb: theme.spacing(3) }}
            >
                <Stack direction="row" alignItems="center" gap={theme.spacing(2)}>
                    <Typography variant="h3">{t("settings.action_plan_templates.templates", "Templates")}</Typography>
                    {total !== undefined && <FoodsTags label={String(total)} />}
                </Stack>
                {canCreate && (
                    <Button
                        variant="primary"
                        onClick={() => setCreating(true)}
                        startIcon={<FoodsIcon icon="plus" size={1} />}
                    >
                        {t("settings.action_plan_templates.create", "Create a template")}
                    </Button>
                )}
            </Stack>
            {query.isError ?
                <Stack role="alert" gap={theme.spacing(2)}>
                    <Typography>
                        {t("settings.action_plan_templates.load_failed", "Unable to load templates. Please try again.")}
                    </Typography>
                    <Button variant="secondary" onClick={() => query.refetch()}>
                        {t("settings.action_plan_templates.retry", "Try again")}
                    </Button>
                </Stack>
            :   <MRTGrid<ActionPlanTemplate>
                    layoutMode="semantic"
                    columns={columns}
                    data={templates}
                    getRowId={(template) => template.id}
                    manualPagination
                    manualSorting
                    autoResetPageIndex={false}
                    rowCount={total ?? 0}
                    state={{ pagination, sorting, isLoading: query.isLoading, showProgressBars: query.isFetching }}
                    onPaginationChange={setPagination}
                    onSortingChange={(updater) => {
                        setSorting(updater);
                        setPagination((current) => ({ ...current, pageIndex: 0 }));
                    }}
                    enableMultiSort={false}
                    enableSortingRemoval={false}
                    sortDescFirst={false}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableColumnResizing={false}
                    enableHiding={false}
                    enableRowSelection={false}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enableRowActions
                    positionActionsColumn="last"
                    displayColumnDefOptions={{
                        "mrt-row-actions": {
                            header: t("settings.action_plan_templates.column.actions", "Actions"),
                            size: 100,
                        },
                    }}
                    muiTableProps={{ "aria-label": title, "aria-busy": query.isFetching }}
                    muiPaginationStackProps={{
                        sx: { display: (total ?? 0) > ACTION_PLAN_TEMPLATES_PAGE_SIZE ? "flex" : "none" },
                    }}
                    localization={{
                        noRecordsToDisplay:
                            query.isLoading ?
                                t("settings.action_plan_templates.loading", "Loading templates…")
                            :   t("settings.action_plan_templates.empty", "No templates created yet."),
                    }}
                    muiTableBodyRowProps={({ row }) => ({
                        onClick: () => {
                            if (row.original.canView) openTemplate(row.original.id);
                        },
                        tabIndex: row.original.canView ? 0 : undefined,
                        onKeyDown: (event) => {
                            if (event.target === event.currentTarget && event.key === "Enter" && row.original.canView)
                                openTemplate(row.original.id);
                        },
                        sx: { cursor: row.original.canView ? "pointer" : "default" },
                    })}
                    renderRowActions={({ row }) => (
                        <TemplateActionsMenu
                            template={row.original}
                            canCreate={canCreate}
                            onView={() => openTemplate(row.original.id)}
                            onEdit={() =>
                                navigate(
                                    `${URLHandler.settings.configurationPath("platform-setup", "action-plan-templates")}/${row.original.id}/edit`,
                                )
                            }
                            onDeleted={() => {
                                if (templates.length === 1)
                                    setPagination((current) => ({
                                        ...current,
                                        pageIndex: Math.max(0, current.pageIndex - 1),
                                    }));
                            }}
                        />
                    )}
                />
            }
            {creating && canCreate && (
                <CreateTemplateDialog
                    onClose={() => setCreating(false)}
                    onCreated={(id) => {
                        setCreating(false);
                        openTemplate(id);
                    }}
                />
            )}
        </Box>
    );
};
